import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import {
    reverseSessionSubdomain,
    getSubdomainFromSession,
    getCustomization,
} from "components/helper/callApi";
import { useSubdomain } from "components/helper/SubdomainContext";

import {
    buildVersionedFootprint,
    clearCustomization,
    Customization,
    saveCustomization,
    Subdomain,
} from "services/customizationService";
import {
    getSessionSubdomainRedirectUrl,
    getSubdomain,
} from "services/urlService";

import { ERROR } from "types/snackBar.constant";
import { LS_FOOTPRINT_KEY } from "constants/constants";

const SubdomainHandler = ({ children }: any) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setSubdomainHandlingCompleted } = useSubdomain();

    const subdomain = getSubdomain();
    const currentPath = window.location.pathname;

    const handleSubdomainCodeOnline = async () => {
        try {
            const codeOnline: string | null = await reverseSessionSubdomain();
            if (!codeOnline) return;
            navigate(`/login/${codeOnline}`);
        } catch (error) {
            enqueueSnackbar(`${t("errorUnknown")}`, {
                variant: ERROR,
            });
            return;
        }
    };

    /**
     * Redirects to the subdomain of the session's entity, unless the participant is already on it.
     * Returns true when a redirection has been triggered.
     */
    const redirectToSubdomain = async (
        codeOnline: string,
    ): Promise<boolean> => {
        try {
            if (!codeOnline) {
                return false;
            }

            const data: Subdomain | null =
                await getSubdomainFromSession(codeOnline);
            if (!data) return false;

            const redirectUrl: string | null = getSessionSubdomainRedirectUrl(
                data,
                subdomain,
            );
            if (!redirectUrl) return false;

            window.location.replace(redirectUrl);
            return true;
        } catch (error) {
            enqueueSnackbar(`${t("errorUnknown")}`, {
                variant: ERROR,
            });
            return false;
        }
    };

    const handleCustomization = async (customization: Customization) => {
        try {
            const existingFootprint = localStorage.getItem(LS_FOOTPRINT_KEY);

            if (
                buildVersionedFootprint(customization.footprint) ===
                existingFootprint
            ) {
                // Entity didn't changed, no need to save it
                return;
            }

            saveCustomization(customization);
        } catch (error) {
            enqueueSnackbar(`${t("errorUnknown")}`, {
                variant: ERROR,
            });
        }
    };

    useEffect(() => {
        const init = async () => {
            setSubdomainHandlingCompleted(false);

            try {
                if (subdomain) {
                    const customization: Customization | null =
                        await getCustomization();
                    const baseUrl = window.env
                        .REACT_APP_ONLINE_API_BASE_URL as string;
                    if (!customization) {
                        // No customization (none for this entity, network or API error): the previous one must not stay displayed.
                        clearCustomization();
                    }

                    if (!customization && !baseUrl.includes("local")) {
                        window.location.replace(`${baseUrl}${currentPath}`);
                        return;
                    }

                    // The subdomain may belong to another entity than the session's one: redirect before saving its
                    // customization, otherwise the participant would get the design of the wrong entity.
                    if (
                        currentPath.includes(`/login/`) &&
                        (await redirectToSubdomain(
                            currentPath.split("/login/")[1],
                        ))
                    ) {
                        return;
                    }

                    if (currentPath === "/") {
                        await handleSubdomainCodeOnline();
                    }

                    if (customization) {
                        await handleCustomization(customization);
                    }
                } else {
                    // Without a subdomain the entity is unknown: a customization stored earlier (socket update,
                    // previous visit) would otherwise be applied to every session of this origin.
                    clearCustomization();

                    if (currentPath.includes(`/login/`)) {
                        await redirectToSubdomain(
                            currentPath.split("/login/")[1],
                        );
                    }
                }
            } catch (error) {
                enqueueSnackbar(`${t("errorUnknown")}`, {
                    variant: ERROR,
                });
            } finally {
                setSubdomainHandlingCompleted(true);
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subdomain, currentPath]);

    return children;
};

export default SubdomainHandler;
